import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export type ResellerOrderStatus = 'pending' | 'approved' | 'activated' | 'rejected' | 'failed' | 'hold';

export interface ResellerOrderRow {
  id: string;
  order_number: string;
  reseller_id: string;
  customer_email: string;
  customer_name: string | null;
  business_name: string | null;
  contact_phone: string | null;
  domain: string | null;
  plan_id: string | null;
  amount: number;
  currency: string;
  status: ResellerOrderStatus;
  notes: string | null;
  license_id: string | null;
  activated_at: string | null;
  activated_by: string | null;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface LicenseRow {
  id: string;
  license_key: string;
  owner_user_id: string;
  plan_id: string | null;
  status: string;
  domain: string | null;
  issued_at: string;
  expires_at: string | null;
  activated_at: string | null;
  metadata: Record<string, unknown>;
}

export function useMyResellerOrders() {
  return useQuery({
    queryKey: ['reseller-orders', 'mine'],
    queryFn: async (): Promise<ResellerOrderRow[]> => {
      const { data, error } = await supabase
        .from('reseller_orders')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as ResellerOrderRow[];
    },
  });
}

export function useAllResellerOrders(status?: ResellerOrderStatus) {
  return useQuery({
    queryKey: ['reseller-orders', 'all', status ?? 'any'],
    queryFn: async (): Promise<ResellerOrderRow[]> => {
      let query = supabase.from('reseller_orders').select('*').order('created_at', { ascending: false });
      if (status) query = query.eq('status', status);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as ResellerOrderRow[];
    },
  });
}

export function useMyLicenses() {
  return useQuery({
    queryKey: ['reseller-licenses', 'mine'],
    queryFn: async (): Promise<LicenseRow[]> => {
      const { data, error } = await supabase
        .from('licenses')
        .select('*')
        .order('issued_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as LicenseRow[];
    },
  });
}

export function useCreateResellerOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      customer_email: string;
      customer_name?: string;
      business_name?: string;
      contact_phone?: string;
      domain?: string;
      plan_id?: string;
      amount: number;
      currency?: string;
      notes?: string;
    }) => {
      const { data, error } = await supabase.functions.invoke('reseller-create-order', { body: payload });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reseller-orders'] });
      toast.success('Order created');
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useApproveResellerOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (order_id: string) => {
      const { data, error } = await supabase.functions.invoke('reseller-approve-order', { body: { order_id } });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reseller-orders'] });
      toast.success('Order approved');
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useActivateResellerOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (order_id: string) => {
      const { data, error } = await supabase.functions.invoke('reseller-activate-order', {
        body: { order_id, send_email: true },
      });
      if (error) throw error;
      return data as { license_key: string };
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['reseller-orders'] });
      qc.invalidateQueries({ queryKey: ['reseller-licenses'] });
      toast.success(`Activated — license ${data.license_key}`);
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useResellerStats(orders: ResellerOrderRow[] | undefined) {
  const list = orders ?? [];
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  return {
    pending: list.filter((o) => o.status === 'pending').length,
    approved: list.filter((o) => o.status === 'approved').length,
    activated: list.filter((o) => o.status === 'activated').length,
    totalRevenue: list
      .filter((o) => o.status === 'activated')
      .reduce((s, o) => s + Number(o.amount ?? 0), 0),
    monthRevenue: list
      .filter((o) => o.status === 'activated' && new Date(o.created_at) >= monthStart)
      .reduce((s, o) => s + Number(o.amount ?? 0), 0),
    uniqueCustomers: new Set(list.map((o) => o.customer_email)).size,
  };
}

/**
 * @deprecated Legacy mock hook shape kept only to preserve typecheck for old
 * MasterLicense reseller pages. Use useMyResellerOrders / useAllResellerOrders.
 */
export function useResellerOrders() {
  return { orders: [] as Array<import('@/types/resellerOrder').ResellerOrder>, isLoading: false };
}
